import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Container,
  Avatar,
  Rating,
  Stack,
  Grid,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  FormatQuote,
  Star,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';

const Testimonials = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const scrollContainerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials = [
    {
      id: 1,
      name: 'Amadou Diallo',
      role: 'Entrepreneur',
      location: 'Tchamba',
      image: null,
      rating: 5,
      text: 'Tchamba Marketplace a transformé mon business ! Je peux maintenant vendre mes produits facilement et toucher beaucoup plus de clients locaux. La plateforme est intuitive et le service client excellent.',
      date: 'Il y a 2 semaines'
    },
    {
      id: 2,
      name: 'Fatou Traoré',
      role: 'Vendeuse',
      location: 'Tchamba',
      image: null,
      rating: 5,
      text: 'Excellente plateforme ! J\'ai pu créer ma boutique en quelques minutes et commencer à vendre immédiatement. Les clients peuvent me contacter facilement via WhatsApp. Je recommande vivement !',
      date: 'Il y a 1 mois'
    },
    {
      id: 3,
      name: 'Ibrahim Sall',
      role: 'Commerçant',
      location: 'Tchamba',
      image: null,
      rating: 5,
      text: 'La meilleure marketplace locale ! Les produits sont authentiques et les vendeurs très réactifs. Une vraie révolution pour le commerce à Tchamba.',
      date: 'Il y a 3 semaines'
    },
    {
      id: 4,
      name: 'Aissatou Kane',
      role: 'Artisan',
      location: 'Tchamba',
      image: null,
      rating: 5,
      text: 'En tant qu\'artisan, je cherchais une plateforme pour vendre mes créations. Tchamba Marketplace est parfaite ! Mes produits sont bien présentés et j\'ai déjà plusieurs commandes. Merci !',
      date: 'Il y a 1 semaine'
    },
    {
      id: 5,
      name: 'Moussa Ba',
      role: 'Entrepreneur',
      location: 'Tchamba',
      image: null,
      rating: 5,
      text: 'Formule d\'abonnement très avantageuse ! J\'ai choisi la formule Premium et j\'ai pu créer plusieurs boutiques avec un nombre illimité de produits. Exceptionnel !',
      date: 'Il y a 5 jours'
    },
    {
      id: 6,
      name: 'Mariama Diop',
      role: 'Commerçante',
      location: 'Tchamba',
      image: null,
      rating: 5,
      text: 'Service client au top ! À chaque fois que j\'ai une question, l\'équipe répond rapidement et avec professionnalisme. Une plateforme qui prend soin de ses utilisateurs.',
      date: 'Il y a 2 mois'
    }
  ];

  // Générer des initiales pour les avatars
  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Générer une couleur de fond pour l'avatar
  const getAvatarColor = (id) => {
    const colors = [
      'linear-gradient(135deg, #f97316 0%, #ef4444 100%)',
      'linear-gradient(135deg, #ef4444 0%, #ec4899 100%)',
      'linear-gradient(135deg, #ec4899 0%, #a855f7 100%)',
      'linear-gradient(135deg, #a855f7 0%, #6366f1 100%)',
      'linear-gradient(135deg, #6366f1 0%, #3b82f6 100%)',
      'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)'
    ];
    return colors[(id - 1) % colors.length];
  };

  // Calculer le nombre de cartes visibles
  const getVisibleCards = () => {
    if (isMobile) return 1;
    if (isTablet) return 2;
    return 3;
  };

  // Vérifier les boutons de navigation
  const checkScrollButtons = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      const tolerance = 5;
      setCanScrollLeft(scrollLeft > tolerance);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - tolerance);
    }
  }, []);

  // Navigation
  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      const firstChild = scrollContainerRef.current.children[0];
      const cardWidth = firstChild ? firstChild.offsetWidth : 320;
      const gap = 24;
      scrollContainerRef.current.scrollBy({ 
        left: -(cardWidth + gap),
        behavior: 'smooth' 
      });
      setCurrentIndex(prev => Math.max(0, prev - 1));
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      const firstChild = scrollContainerRef.current.children[0];
      const cardWidth = firstChild ? firstChild.offsetWidth : 320;
      const gap = 24;
      scrollContainerRef.current.scrollBy({ 
        left: cardWidth + gap,
        behavior: 'smooth' 
      });
      setCurrentIndex(prev => Math.min(testimonials.length - getVisibleCards(), prev + 1));
    }
  };

  useEffect(() => {
    checkScrollButtons();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      window.addEventListener('resize', checkScrollButtons);
      return () => {
        container.removeEventListener('scroll', checkScrollButtons);
        window.removeEventListener('resize', checkScrollButtons);
      };
    }
  }, [checkScrollButtons]);

  return (
    <Box
      sx={{
        py: 8,
        background: 'linear-gradient(180deg, rgba(255, 255, 255, 1) 0%, rgba(249, 250, 251, 1) 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background decorative elements */}
      <Box
        sx={{
          position: 'absolute',
          top: -50,
          right: -50,
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.08) 0%, rgba(236, 72, 153, 0.08) 100%)',
          filter: 'blur(60px)',
          zIndex: 0
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -50,
          left: -50,
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.08) 0%, rgba(249, 115, 22, 0.08) 100%)',
          filter: 'blur(60px)',
          zIndex: 0
        }}
      />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Stack spacing={2} alignItems="center">
            <Chip
              label="Témoignages"
              sx={{
                bgcolor: 'rgba(249, 115, 22, 0.1)',
                color: '#f97316',
                fontWeight: 600,
                px: 2,
                py: 3,
                fontSize: '0.875rem'
              }}
            />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 1,
                background: 'linear-gradient(90deg, #f97316 0%, #ef4444 50%, #ec4899 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Ce que disent nos utilisateurs
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600 }}>
              Découvrez les expériences réelles de nos clients qui font confiance à Tchamba Marketplace
            </Typography>
          
          </Stack>
        </Box>

        {/* Testimonials Carousel */}
        <Box sx={{ position: 'relative', px: { xs: 0, sm: 4 } }}>
          {/* Navigation Buttons */}
          {testimonials.length > getVisibleCards() && (
            <>
              {canScrollLeft && (
                <IconButton
                  onClick={scrollLeft}
                  sx={{
                    position: 'absolute',
                    left: { xs: -10, sm: -20 },
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 10,
                    bgcolor:'linear-gradient(90deg, #f97316 0%, #ef4444 50%, #ec4899 100%)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    width: 48,
                    height: 48,
                    '&:hover': {
                      background: 'linear-gradient(90deg, #f97316 0%, #ef4444 50%, #ec4899 100%)',
                      color: 'white',
                      transform: 'translateY(-50%) scale(1.1)',
                      boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
                    },
                    transition: 'all 0.3s ease',
                    border: '1px solid rgba(0,0,0,0.1)'
                  }}
                >
                  <ChevronLeft fontSize="large" />
                </IconButton>
              )}
              {canScrollRight && (
                <IconButton
                  onClick={scrollRight}
                  sx={{
                    position: 'absolute',
                    right: { xs: -10, sm: -20 },
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 10,
                    bgcolor:'linear-gradient(90deg, #f97316 0%, #ef4444 50%, #ec4899 100%)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    width: 48,
                    height: 48,
                    '&:hover': {
                      background:'linear-gradient(90deg, #f97316 0%, #ef4444 50%, #ec4899 100%)',
                      color: 'white',
                      transform: 'translateY(-50%) scale(1.1)',
                      boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
                    },
                    transition: 'all 0.3s ease',
                    border: '1px solid rgba(0,0,0,0.1)'
                  }}
                >
                  <ChevronRight fontSize="large" />
                </IconButton>
              )}
            </>
          )}

          {/* Scrollable Container */}
          <Box
            ref={scrollContainerRef}
            sx={{
              display: 'flex',
              gap: 3,
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollSnapType: 'x mandatory',
              scrollBehavior: 'smooth',
              pb: 2,
              // Scrollbar invisible
              '&::-webkit-scrollbar': {
                display: 'none'
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '& > *': {
                flexShrink: 0,
                scrollSnapAlign: 'start'
              }
            }}
            onScroll={checkScrollButtons}
          >
            {testimonials.map((testimonial, index) => (
              <Box
                key={testimonial.id}
                sx={{
                  width: { xs: '90%', sm: '45%', md: '32%' },
                  minWidth: { xs: '90%', sm: '45%', md: '32%' },
                  animation: `slideIn 0.6s ease ${index * 0.1}s both`,
                  '@keyframes slideIn': {
                    from: {
                      opacity: 0,
                      transform: 'translateX(30px) scale(0.95)'
                    },
                    to: {
                      opacity: 1,
                      transform: 'translateX(0) scale(1)'
                    }
                  }
                }}
              >
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 3,
                    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    overflow: 'hidden',
                    '&:before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      background: getAvatarColor(testimonial.id),
                      zIndex: 1
                    },
                    '&:hover': {
                      transform: 'translateY(-6px) scale(1.02)',
                      boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 2.5, pt: 3.5 }}>
                    {/* Quote Icon */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        opacity: 0.08,
                        zIndex: 0
                      }}
                    >
                      <FormatQuote sx={{ fontSize: 50, transform: 'rotate(180deg)' }} />
                    </Box>

                    {/* Rating */}
                    <Box sx={{ mb: 1.5, position: 'relative', zIndex: 1 }}>
                      <Rating 
                        value={testimonial.rating} 
                        readOnly 
                        size="small"
                        sx={{ 
                          '& .MuiRating-iconFilled': {
                            color: '#f97316'
                          }
                        }}
                      />
                    </Box>

                    {/* Testimonial Text */}
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 2,
                        color: 'text.primary',
                        lineHeight: 1.7,
                        minHeight: 60,
                        position: 'relative',
                        zIndex: 1,
                        fontSize: '0.875rem'
                      }}
                    >
                      {testimonial.text}
                    </Typography>

                    {/* User Info */}
                    <Stack 
                      direction="row" 
                      spacing={1.5} 
                      alignItems="center"
                      sx={{ 
                        pt: 1.5,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        position: 'relative',
                        zIndex: 1
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 44,
                          height: 44,
                          background: getAvatarColor(testimonial.id),
                          fontWeight: 600,
                          fontSize: '1rem'
                        }}
                      >
                        {getInitials(testimonial.name)}
                      </Avatar>
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.25, fontSize: '0.875rem' }}>
                          {testimonial.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', display: 'block' }}>
                          {testimonial.role} • {testimonial.location}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                          {testimonial.date}
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Bottom CTA */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Chip
            label=" Rejoignez nos utilisateurs satisfaits"
            icon={<Star sx={{ fontSize: 18 }} />}
            sx={{
              bgcolor: 'rgba(249, 115, 22, 0.1)',
              color: '#f97316',
              fontWeight: 600,
              px: 2
            }}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default Testimonials;

